import { Injectable, NgZone } from '@angular/core';
import { act, Actions, createEffect, ofType } from '@ngrx/effects';
import { map, exhaustMap, catchError, tap, withLatestFrom, mergeMap, filter, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { ErrorService } from '../../services/error.service';
import { failedTransaction, setLoadingSpinner } from '../actions/shared.actions';
import { ModalController, NavController, ToastController } from '@ionic/angular';
import { RouterNavigatedAction, ROUTER_NAVIGATION } from '@ngrx/router-store';
import { APIResponse } from 'src/app/models/api-response.model';
import { RideService } from 'src/app/services/ride.service';
import * as fromOrderAction from '../actions/order.action';
import * as fromTokenAction from '../actions/tokens.actions';
import * as fromPassesAction from '../actions/passes.action';
import { NavigationExtras, Router } from '@angular/router';
import { OrderService } from 'src/app/services/order.service';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { OfflineStorageService } from 'src/app/services/offline-storage.service';
import { getUserOrganizationPoints } from '../actions/organization.actions';
import { reloadUserPass, updatePassCart, updatePassCartSuccess } from '../actions/passes.action';
import { DynamicSuccessComponent } from 'src/app/shared/modals/dynamic-success/dynamic-success.component';
import { Browser } from '@capacitor/browser';

@Injectable()
export class OrderEffects {

  createShopeePayOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromOrderAction.createShopeePayOrder),
      exhaustMap((action) =>
        this.orderService.shopeepayCreateOrder(action.data).pipe(
          map((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return fromOrderAction.createShopeePayOrderSuccess({ data: response?.details, itemType: action?.itemType });
          }),
          catchError((response) => {
            console.error('createShopeePayOrder$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response)
            };

            return of(failedTransaction({ data: error }));
          })
        )
      )
    )
  );

  createShopeePayOrderSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromOrderAction.createShopeePayOrderSuccess),
        tap((action) => {
          console.log('createShopeePayOrderSuccess$', action.data);
          const pendingOrder = {
            redirect_url_app: action?.data?.redirect_url_app,
            redirect_url_http: action?.data?.redirect_url_http,
            reference_id: action?.data?.reference_id,
            expiry_time: action?.data?.expiry_time,
          }

          const itemType = action?.itemType;

          this.offlineStorage.setObject('pendingOrder', pendingOrder);
          const inAppBrowser = this.iab.create(action?.data?.redirect_url_http, '_system');

          inAppBrowser.on('exit').subscribe(event => {
            this.ngZone.run(() => {
              console.log('exit iab');
              this.store.dispatch(fromTokenAction.reloadUserTokens());
              this.store.dispatch(getUserOrganizationPoints());
              this.store.dispatch(reloadUserPass());

              if(itemType === 'token'){
                this.store.dispatch(fromTokenAction.clearTokenCart());
                this.offlineStorage.removeItem('token:cart');
                this.navCtrl.navigateBack('/home/tokens');
                this.store.dispatch(fromTokenAction.updateTokenCart({data: ''}));
              }
              else{
                this.store.dispatch(fromPassesAction.clearPassCart());
                this.offlineStorage.removeItem('pass:cart');
                this.store.dispatch(updatePassCart({data: ''}));
                this.navCtrl.navigateBack('/home/passes');
              }
            })
          });
        })
      ),
    { dispatch: false }
  );

  createGcashOder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromOrderAction.createGcashOrder),
      exhaustMap((action) =>
        this.orderService.gCashCreateOrder(action.data).pipe(
          map((response) => {
            console.log({response});
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return fromOrderAction.createGcashOrderSuccess({ data: response?.details, itemType: action?.itemType});
          }),
          catchError((response) => {
            console.error('createGcashOder$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response)
            };
            return of(failedTransaction({ data: error }));
          })
        )
      )
    )
  );

  createGcashOderSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromOrderAction.createGcashOrderSuccess),
        tap(async (action) => {
          console.log('createGcashOderSuccess$', action);
          const isModalOpen = await this.modalController.getTop();
          if(isModalOpen){
            this.modalController.dismiss();
          }

          const pendingOrder = {
            redirect_url_http: action?.data?.response.body.checkoutUrl,
            reference_id: action?.data?.response.body.merchantTransId,
            expiry_time: '',
          }

          const itemType = action?.itemType;

          if(itemType === 'token'){
            this.store.dispatch(fromTokenAction.clearTokenCart());
            this.offlineStorage.removeItem('token:cart');
            this.store.dispatch(fromTokenAction.updateTokenCart({data: ''}));
          }
          else{
            this.store.dispatch(fromPassesAction.clearPassCart());
            this.store.dispatch(updatePassCart({data: ''}));
            this.offlineStorage.removeItem('pass:cart');
          }

          this.offlineStorage.setObject('pendingOrder', pendingOrder);
          let checkoutUrl = action?.data?.response.body.checkoutUrl;
          checkoutUrl = checkoutUrl.replace('http://','https://');

          await Browser.open({ url: checkoutUrl });

          Browser.addListener('browserFinished', () => {
            this.ngZone.run(() => {
              this.store.dispatch(getUserOrganizationPoints());
              this.store.dispatch(fromTokenAction.reloadUserTokens());
              this.store.dispatch(reloadUserPass());
              this.navCtrl.navigateBack('/home/bottom-tabs/transaction-history');
            });
          });
        })
      ),
    { dispatch: false }
  );

  createMayaOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromOrderAction.createMayaOrder),
      exhaustMap((action) =>
        this.orderService.mayaCreateOrder(action.data).pipe(
          map((response) => {
            console.log({response});
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return fromOrderAction.createMayaOrderSuccess({ data: response?.details, itemType: action?.itemType});
          }),
          catchError((response) => {
            console.error('createMayaOrder$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response)
            };
            return of(failedTransaction({ data: error }));
          })
        )
      )
    )
  );

  createMayaOrderSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromOrderAction.createMayaOrderSuccess),
        tap(async (action) => {
          console.log('createMayaOrderSuccess$', action);

          const pendingOrder = {
            redirect_url_http: action?.data?.redirectUrl,
            checkout_id: action?.data?.checkoutId,
            request_reference_number: action?.data?.requestReferenceNumber,
          }

          const itemType = action?.itemType;

          this.offlineStorage.setObject('pendingOrder', pendingOrder);
          let checkoutUrl = action?.data?.redirectUrl;

          await Browser.open({ url: checkoutUrl });

          Browser.addListener('browserFinished', () => {
            this.ngZone.run(() => {
              console.log('exit iab');
              this.store.dispatch(getUserOrganizationPoints());
              this.store.dispatch(fromTokenAction.reloadUserTokens());
              this.store.dispatch(reloadUserPass());
              this.navCtrl.navigateBack('/home/bottom-tabs/transaction-history');
            })
          });
        })
      ),
    { dispatch: false }
  );

  createGetpassOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromOrderAction.createGetpassOrder),
      exhaustMap((action) =>
        this.orderService.getPassCreateOrder(action.data).pipe(
          map((response) => {
            console.log({response});
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return fromOrderAction.createGetpassOrderSuccess({ data: response?.details, itemType: action?.itemType});
          }),
          catchError((response) => {
            console.error('createGetpassOrder$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response)
            };
            return of(failedTransaction({ data: error }));
          })
        )
      )
    )
  );

  createGetpassOrderSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromOrderAction.createGetpassOrderSuccess),
        tap((action) => {
          console.log('createGetpassOrderSuccess$', action);
          this.store.dispatch(getUserOrganizationPoints());
          this.store.dispatch(fromTokenAction.reloadUserTokens());
          this.store.dispatch(reloadUserPass());

          const itemType = action?.itemType;

          if(itemType === 'token'){
            this.store.dispatch(fromTokenAction.clearTokenCart());
            this.offlineStorage.removeItem('token:cart');
            this.navCtrl.navigateBack('/home/tokens');
            this.store.dispatch(fromTokenAction.updateTokenCart({data: ''}));
          }
          else{

            this.successModal('Success!', "Successful payment", 'Continue');
            this.store.dispatch(fromPassesAction.clearPassCart());
            this.offlineStorage.removeItem('pass:cart');
            this.store.dispatch(updatePassCart({data: ''}));
            this.navCtrl.navigateBack('/home/passes');
          }
        })
      ),
    { dispatch: false }
  );

  async successModal(title: string, content: string, okButtonLabel: string){
    const modal = await this.modalController.create({
      component: DynamicSuccessComponent,
      componentProps: {
        title: title,
        content: content,
        okButtonLabel: okButtonLabel
      },
      cssClass: 'delete-account-confirm',
      showBackdrop: true,
      backdropDismiss: true,
      keyboardClose: true,
      initialBreakpoint: 1,
      breakpoints: [0, 0.5, 1],
      handle: false
    });

    return modal.present();
  }

  getOrderHistory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      filter((r : RouterNavigatedAction) => {
        return r.payload.routerState.url.startsWith('/home/bottom-tabs/token/token-order-history')
      }),
      mergeMap(() => {
        return this.orderService.orderHistory().pipe(
          map((response: APIResponse) => {           
            console.log('getOrderHistory$', response);
            return fromOrderAction.getOrderHistorySuccess({ data: response.details});
          })
        );
      })
    )
  });

  reloadOrderHistory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromOrderAction?.reloadOrderHistory),
      mergeMap(() => {
        return this.orderService.orderHistory().pipe(
          map((response: APIResponse) => {           
            console.log('reloadOrderHistory$', response);
            return fromOrderAction.getOrderHistorySuccess({ data: response.details});
          })
        );
      })
    )
  });

  checkOrderStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromOrderAction.checkOrderStatus),
      exhaustMap((action) =>
        this.orderService.checkOrderStatus(action.data).pipe(
          map((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            this.store.dispatch(fromTokenAction.reloadUserTokens());
            this.store.dispatch(getUserOrganizationPoints());
            return fromOrderAction.checkOrderStatusSuccess({ data: response?.details});
          }),
          catchError((response) => {
            console.error('checkOrderStatus$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response)
            };
            return of(failedTransaction({ data: error }));
          })
        )
      )
    )
  );

  checkOrderStatusSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromOrderAction.checkOrderStatusSuccess),
        tap(async (action) => {
          console.log('checkOrderStatusSuccess$', action);
          // const modal = await this.modalController.create({
          //   component: TransactionDetailsComponent,
          //   cssClass: 'transaction-details-modal',
          //   componentProps: {
          //     transaction: action?.data
          //   }
          // });
          // modal.onDidDismiss().then(() => {
          //   this.store.dispatch(fromOrderAction.reloadOrderHistory());
          //   this.store.dispatch(reloadUserPass());
          //   this.store.dispatch(fromTokenAction.reloadUserTokens());
          //   this.store.dispatch(getUserOrganizationPoints());
          // })
          // return modal.present();
        })
      ),
    { dispatch: false }
  );

  cancelShopeePayOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromOrderAction.cancelShopeePayOrder),
      exhaustMap((action) =>
        this.orderService.cancelShopeePayOrder(action.data).pipe(
          map((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return fromOrderAction.cancelShopeePayOrderSuccess({ data: response?.details});
          }),
          catchError((response) => {
            console.error('cancelShopeePayOrder$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response)
            };
            return of(failedTransaction({ data: error }));
          })
        )
      )
    )
  );

  cancelShopeePayOrderSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromOrderAction.cancelShopeePayOrderSuccess),
        tap(async (action) => {
          console.log('cancelShopeePayOrderSuccess$', action?.data);
          this.store.dispatch(fromOrderAction.reloadOrderHistory());
          this.modalController.dismiss();
          this.navCtrl.navigateBack('home/bottom-tabs/token/token-order-history/0');
        })
      ),
    { dispatch: false }
  );

  cancelGcashOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromOrderAction.cancelGcashOrder),
      exhaustMap((action) =>
        this.orderService.cancelGcashOrder(action.data).pipe(
          map((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return fromOrderAction.cancelGcashOrderSuccess({ data: response?.details});
          }),
          catchError((response) => {
            console.error('cancelGcashOrder$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response)
            };
            return of(failedTransaction({ data: error }));
          })
        )
      )
    )
  );

  cancelGcashOrderSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromOrderAction.cancelGcashOrderSuccess),
        tap(async (action) => {
          console.log('cancelGcashOrderSuccess$', action?.data);
          this.store.dispatch(fromOrderAction.reloadOrderHistory());
          this.modalController.dismiss();
          this.navCtrl.navigateBack('home/bottom-tabs/token/token-order-history/0');
        })
      ),
    { dispatch: false }
  );
    
  async presentToast(msg, pos, style) {
      const toast = await this.toastController.create({
      message: msg,
      duration: 3000,
      position: pos,
      cssClass: style
      });
      toast.present();
  }

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private errorService: ErrorService,
    private orderService: OrderService,
    private toastController: ToastController,
    private navCtrl: NavController,
    private modalController: ModalController,
    private iab: InAppBrowser,
    private offlineStorage: OfflineStorageService,
    private ngZone: NgZone,
    public router: Router,
  ) {}
}

